import classNames from 'classnames';
import * as _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { setupForDark } from '@theme';
import { useEffect, useState } from 'react';
import InputErrorMessage from './InputErrorMessage';

function NumberInput({
  displayMode = 'default',
  type = 'INTEGER',
  value,
  minValue,
  maxValue,
  precision = 1,
  step = 1,
  onChange,
  onError
}: {
  displayMode?: 'default' | 'minimalistic';
  type?: 'INTEGER' | 'FLOAT';
  value: number;
  minValue?: number;
  maxValue?: number;
  precision?: number;
  step?: number;
  onChange: (value: number) => void;
  onError?: (error: string) => void;
}) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const onErrorMessage = (error: string) => {
    if (onError) {
      onError(error);
    }
    setError(error);
  };

  const validate = (v: number): boolean => {
    if (_.isNaN(v) && (_.isNil(minValue) || _.isNil(maxValue))) {
      onErrorMessage(t('components.numberInput.errorWrongValue', { min: minValue, max: maxValue }));
      return false;
    }

    if (_.isNaN(v) && !_.isNil(minValue) && !_.isNil(maxValue)) {
      onErrorMessage(
        t('components.numberInput.errorWrongValueFromRange', { min: minValue, max: maxValue })
      );
      return false;
    }

    if (type === 'INTEGER' && step && v % step !== 0) {
      onErrorMessage(t('components.numberInput.errorNotMultiplicityOfStep', { step: step }));
      return false;
    }

    if (!_.isNil(minValue) && !_.isNil(maxValue) && (v < minValue || v > maxValue)) {
      onErrorMessage(
        t('components.numberInput.errorWrongValueFromRange', { min: minValue, max: maxValue })
      );
      return false;
    }

    onErrorMessage(null);
    return true;
  };

  useEffect(() => {
    if (type === 'INTEGER') {
      setInputValue(parseInt(value.toFixed()));
    } else {
      setInputValue(value);
    }
  }, [value, type]);

  // check for errors only if the minValue, maxValue or inputValue changes
  useEffect(() => {
    validate(inputValue);
  }, [minValue, maxValue, inputValue, type]);

  const onValueChange = (event) => {
    let raw;
    if (type === 'FLOAT') {
      raw = parseFloat(event.target.value);
    } else {
      raw = parseInt(event.target.value);
    }

    setInputValue(raw);
    if (validate(raw)) {
      onChange(raw);
    }
  };

  const onBlur = (event) => {
    const v = parseInt(event.target.value);

    if (_.isNaN(v)) {
      setInputValue(minValue);
      onChange(minValue);
      onErrorMessage(null);
    }
  };

  const onKeyDown = (e) => {
    let keyCode = e.keyCode || e.which;
    let keyValue = String.fromCharCode(keyCode);

    if (
      e.key === 'Backspace' ||
      keyCode === 8 ||
      e.key === 'Delete' ||
      keyCode === 46 ||
      e.key === 'Tab' ||
      keyCode === 9 ||
      e.key === 'ArrowLeft' ||
      keyCode === 37 ||
      e.key === 'ArrowUp' ||
      keyCode === 38 ||
      e.key === 'ArrowRight' ||
      keyCode === 39 ||
      e.key === 'ArrowDown' ||
      keyCode === 40
    ) {
      return;
    }

    if (type === 'INTEGER' && !/\d/.test(keyValue)) {
      e.preventDefault();
    }

    if (type == 'FLOAT' && inputValue) {
      let value = inputValue.toString() || '';
      let nextValue = value + keyValue;
      let afterComma = nextValue.split(/\.|,/)[1];

      if (afterComma && afterComma.length > precision) {
        e.preventDefault();
      }
    }
  };

  const style = {};
  if (maxValue && displayMode === 'minimalistic') {
    style['maxWidth'] = `${maxValue.toString().length * 7.5}px`;
  }

  const minimalisticClasses = classNames(
    'px-xs rounded-xxs',
    setupForDark('bg-variable-dropdown'),
    setupForDark('text-black'),
    'bg-opacity-15',
    'I2R'
  );

  const defaultClasses = classNames(
    'rounded-xs px-sm py-xs',
    'border-[1px]',
    setupForDark('border-text-input-default'),
    setupForDark('bg-text-input-default'),
    setupForDark('text-primary'),
    'P2M'
  );

  return (
    <div>
      <div
        className={classNames(
          'w-fit',
          'flex flex-row items-center justify-start',
          'cursor-pointer',
          displayMode === 'minimalistic' ? minimalisticClasses : defaultClasses,
          error && onError ? setupForDark('border-error') : '',
          error && onError ? 'border-[1px]' : ''
        )}
      >
        <input
          type='number'
          max={maxValue}
          min={minValue}
          step={step}
          value={inputValue || ''}
          onChange={onValueChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          className={classNames(
            'bg-transparent border-none outline-none hide-input-arrows text-right min-w-[26px]'
          )}
          style={style}
        />
      </div>
      {error && !onError && <InputErrorMessage error={error} />}
    </div>
  );
}

export default NumberInput;
